import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Shield } from 'lucide-react';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-3xl">
              <Shield className="w-8 h-8 text-emerald-600" />
              Privacy Policy
            </CardTitle>
            <p className="text-sm text-muted-foreground">Last Updated: February 17, 2026</p>
          </CardHeader>
          <CardContent className="prose prose-slate max-w-none">
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
              
              <h3 className="text-lg font-medium mt-4 mb-2">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed">
                When you create an account, we collect:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Name and email address</li>
                <li>Business type and filing status</li>
                <li>State of residence</li>
                <li>W-2 and spouse information (if provided)</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">Financial Information</h3>
              <p className="text-gray-700 leading-relaxed">
                To provide tax management services, we store:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Income and expense transactions</li>
                <li>Receipt and document uploads</li>
                <li>Bank transaction data (via Stripe Financial Connections)</li>
                <li>Quarterly estimated tax payments</li>
                <li>Mileage logs and business use of home information</li>
              </ul>

              <h3 className="text-lg font-medium mt-4 mb-2">Usage Data</h3>
              <p className="text-gray-700 leading-relaxed">
                We automatically collect:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>IP address and browser information</li>
                <li>Pages visited and features used</li>
                <li>Error logs and performance metrics</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We use your information to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Provide tax management and calculation services</li>
                <li>Generate tax reports and estimates</li>
                <li>Send transactional emails (receipts, notifications, tax package delivery)</li>
                <li>Improve our AI categorization and recommendation algorithms</li>
                <li>Provide customer support</li>
                <li>Detect and prevent fraud or security issues</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>We will NEVER:</strong>
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Sell your personal or financial information</li>
                <li>Share your data with third parties for marketing</li>
                <li>Use your data to train AI models that serve other users</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">3. Data Storage and Security</h2>
              <p className="text-gray-700 leading-relaxed">
                Your data is stored securely using industry-standard practices:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li><strong>Encryption:</strong> All data is encrypted in transit (TLS/SSL) and at rest</li>
                <li><strong>Database:</strong> MongoDB with access controls and authentication</li>
                <li><strong>Passwords:</strong> Hashed using bcrypt (never stored in plain text)</li>
                <li><strong>Access Control:</strong> Limited to authorized personnel only</li>
                <li><strong>Backups:</strong> Regular automated backups for data recovery</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">4. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                We use the following third-party services to provide our features:
              </p>
              
              <div className="mt-3 space-y-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">Stripe</p>
                  <p className="text-sm text-gray-600">Payment processing and bank connections (read-only transaction data)</p>
                  <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-sm hover:underline">
                    Stripe Privacy Policy →
                  </a>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">Google Gemini AI</p>
                  <p className="text-sm text-gray-600">Transaction categorization and AI advisor chat (via Emergent LLM Key)</p>
                  <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-sm hover:underline">
                    Google Privacy Policy →
                  </a>
                </div>
                
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium text-gray-900">Resend</p>
                  <p className="text-sm text-gray-600">Transactional email delivery</p>
                  <a href="https://resend.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 text-sm hover:underline">
                    Resend Privacy Policy →
                  </a>
                </div>
              </div>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your data for as long as your account is active, plus 7 years after account closure to comply 
                with IRS record-keeping requirements (IRS recommends keeping tax records for 3-7 years).
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                You may request data deletion at any time, but we recommend exporting your data first for your records.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">6. Your Rights (GDPR & CCPA)</h2>
              <p className="text-gray-700 leading-relaxed">
                You have the right to:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li><strong>Access:</strong> Request a copy of your data</li>
                <li><strong>Correction:</strong> Update incorrect information</li>
                <li><strong>Deletion:</strong> Request account and data deletion</li>
                <li><strong>Export:</strong> Download your data in CSV format</li>
                <li><strong>Opt-out:</strong> Unsubscribe from marketing emails (transactional emails will continue)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                To exercise these rights, visit Settings → Privacy & Data, or contact: <strong>privacy@solelytax.ai</strong>
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">7. Cookies and Tracking</h2>
              <p className="text-gray-700 leading-relaxed">
                We use essential cookies for:
              </p>
              <ul className="list-disc pl-6 mt-2 space-y-1 text-gray-700">
                <li>Authentication (JWT tokens)</li>
                <li>Session management</li>
                <li>User preferences (language selection)</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-2">
                We do not use tracking cookies for advertising purposes.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">8. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                SolelyTax AI is not intended for users under 18 years of age. We do not knowingly collect information 
                from children.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">9. Changes to Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy periodically. Users will be notified of material changes via email. 
                Continued use of the Service after changes constitutes acceptance.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3">10. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                For privacy-related questions or to exercise your data rights:
              </p>
              <p className="text-gray-700 mt-2">
                <strong>Email:</strong> privacy@solelytax.ai<br />
                <strong>Support:</strong> support@solelytax.ai
              </p>
            </section>

            <div className="mt-8 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                <strong>Your privacy matters.</strong> We're committed to protecting your financial data and using it only 
                to provide you with the best tax management experience.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Privacy;